import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverKYC() {
  const { api, AdminId } = useGlobalContext();

  const [kycDrivers, setKycDrivers] = useState([]);
  const [kycStatusLoading, setKycStatusLoading] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const kycStepCacheRef = useRef({});

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const mergeStepData = (previousStep = {}, incomingStep = {}) => {
    const merged = { ...previousStep };

    Object.entries(incomingStep || {}).forEach(([key, value]) => {
      // Previous completed-step value ko null/undefined/empty se overwrite mat karo.
      if (value !== null && value !== undefined && value !== "") {
        merged[key] = value;
      }
    });

    return merged;
  };

  /* ================= FETCH ALL DRIVER KYC ================= */

  const fetchDriverKycDetails = useCallback(
    async (preserveLocalUpdates = true) => {
      setLoading(true);

      try {
        const res = await api.post("/api/Driver/v1.0/getAllDriverKycDetails", {
          userid: "",
          adminId: AdminId,
        });

        if (res.data?.responseCode === "00") {
          const serverData = Array.isArray(res.data?.data) ? res.data.data : [];

          if (preserveLocalUpdates) {
            setKycDrivers((prev) =>
              serverData.map((serverDriver) => {
                const local = (prev || []).find(
                  (driver) =>
                    driver.id === serverDriver.id ||
                    driver.driver_code === serverDriver.driver_code,
                );

                if (
                  local &&
                  ["Approved", "Rejected"].includes(local.status) &&
                  serverDriver.status === "Pending"
                ) {
                  return {
                    ...serverDriver,
                    status: local.status,
                  };
                }

                return serverDriver;
              }),
            );
          } else {
            setKycDrivers(serverData);
          }

          return serverData;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(
            res.data?.responseMessage || "Failed to fetch Driver KYC details",
          );
        }

        setKycDrivers([]);
        return [];
      } catch (err) {
        console.error("fetchDriverKycDetails error:", err);
        toast.error("Error fetching Driver KYC details");
        setKycDrivers([]);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [api, AdminId],
  );

  /* ================= VIEW / UPDATE KYC ================= */

  const handleKycAction = useCallback(
    async (payload) => {
      if (!payload) {
        toast.error("KYC payload is required");
        return null;
      }

      setKycStatusLoading(true);

      try {
        const res = await api.post(
          "/api/Driver/v1.0/verifyDriverkycDetails",
          payload,
        );

        if (res.data?.responseCode === "00") {
          toast.success(res.data?.responseMessage || "KYC updated");
          return res.data;
        }

        if (shouldShowLocalError(res.data)) {
          toast.error(res.data?.responseMessage || "KYC update failed");
        }

        return null;
      } catch (err) {
        console.error("handleKycAction error:", err);
        toast.error(err?.message || "KYC update failed");
        return null;
      } finally {
        setKycStatusLoading(false);
      }
    },
    [api],
  );

  /* ================= SUBMIT KYC STEP ================= */

  const handleStepSubmit = async (stepNumber, stepData) => {
    if (!stepNumber || !stepData) {
      toast.error("KYC step data is required");
      return false;
    }

    const userid = String(stepData?.userid || "").trim();

    if (!userid) {
      toast.error("Driver ID is required");
      return false;
    }

    try {
      setKycSubmitting(true);

      const previousData = kycStepCacheRef.current[userid] || {
        step1: {},
        step2: {},
        step3: {},
        step4: {},
        step5: {},
        step6: {},
      };

      const mergedData = {
        step1: mergeStepData(previousData.step1, stepData?.step1),
        step2: mergeStepData(previousData.step2, stepData?.step2),
        step3: mergeStepData(previousData.step3, stepData?.step3),
        step4: mergeStepData(previousData.step4, stepData?.step4),
        step5: mergeStepData(previousData.step5, stepData?.step5),
        step6: mergeStepData(previousData.step6, stepData?.step6),
      };

      const payload = {
        adminId: String(AdminId || ""),
        userid,
        step: String(stepNumber),

        step1: {
          city: mergedData.step1?.city ?? null,
          area: mergedData.step1?.area ?? null,
          pinCode: mergedData.step1?.pinCode ?? null,
        },

        step2: {
          profilePicture: mergedData.step2?.profilePicture ?? null,
        },

        step3: {
          aadharNumber: mergedData.step3?.aadharNumber ?? null,
          aadharFrontPhoto: mergedData.step3?.aadharFrontPhoto ?? null,
          aadharBackPhoto: mergedData.step3?.aadharBackPhoto ?? null,
        },

        step4: {
          panCard: mergedData.step4?.panCard ?? null,
          panCardFrontPhoto: mergedData.step4?.panCardFrontPhoto ?? null,
        },

        step5: {
          drivingLicenseNumber: mergedData.step5?.drivingLicenseNumber ?? null,
          drivingLicenseFrontPhoto:
            mergedData.step5?.drivingLicenseFrontPhoto ?? null,
        },

        step6: {
          transportTypeID: mergedData.step6?.transportTypeID ?? null,
          transportDescription: mergedData.step6?.transportDescription ?? null,
          licensePlate: mergedData.step6?.licensePlate ?? null,
          licensePlatePhoto: mergedData.step6?.licensePlatePhoto ?? null,
        },

        p1: "",
        p2: "",
        p3: "",
        p4: "",
        p5: "",
      };

      console.log(
        `FINAL KYC STEP ${stepNumber} PAYLOAD:`,
        JSON.parse(JSON.stringify(payload)),
      );

      const res = await api.post(
        "/api/Driver/v2.0/addDriverKYCDetails",
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log("ADD DRIVER KYC RESPONSE:", res.data);

      if (res.data?.responseCode === "00") {
        // Server success ke baad hi current merged steps cache karo.
        kycStepCacheRef.current[userid] = mergedData;

        toast.success(
          res.data?.responseMessage || `Step ${stepNumber} saved successfully`,
        );

        return true;
      }

      toast.error(res.data?.responseMessage || "Failed to save KYC details");

      return false;
    } catch (err) {
      console.error("handleStepSubmit error:", err);

      toast.error(
        err?.response?.data?.responseMessage ||
          err?.message ||
          "Error saving KYC data",
      );

      return false;
    } finally {
      setKycSubmitting(false);
    }
  };

  return {
    kycDrivers,
    loading,
    fetchDriverKycDetails,

    kycStatusLoading,
    handleKycAction,

    kycSubmitting,
    handleStepSubmit,
  };
}
